import type { OnboardingStepProps } from '../OnboardingWizard';
import { ArrowBack, ArrowForward, CheckCircle, TrendingUp } from '@mui/icons-material';
import { Alert, Button, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import { useApi } from '@shared/services/api';
import { motion } from 'framer-motion';
import React, { useContext, useState } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';

const PRESET_RATES = [75, 150, 250, 350];

const VALUE_PROPS = [
  'Matched with buyers based on your expertise and availability',
  'Your rate is shared only when you accept an engagement',
  "You're always in control — adjust or remove it any time",
];

export const HourlyRateStep: React.FC<OnboardingStepProps> = ({ onNext, onBack, onSkip }) => {
  const api = useApi();
  const { user } = useContext(UserContext);
  const [rateValue, setRateValue] = useState<string>(user?.hourlyRate?.toString() ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rate = rateValue ? Number.parseFloat(rateValue) : undefined;
  const isValid = !rateValue || (!isNaN(rate!) && rate! > 0);

  const handlePresetClick = (preset: number) => {
    setRateValue(preset.toString());
    setError(null);
  };

  const handleContinue = async () => {
    if (!isValid) {
      setError('Please enter a valid hourly rate (e.g. 150).');
      return;
    }

    if (rateValue && user?.id) {
      setIsSaving(true);
      setError(null);
      try {
        const response = await api.put('user', {
          id: user.id,
          hourlyRate: rate,
        });
        if (response?.error) {
          setError('Failed to save your rate. You can update it later in your profile.');
        }
      } catch {
        // Non-fatal — let them proceed
      } finally {
        setIsSaving(false);
      }
    }

    onNext();
  };

  return (
    <div className="onboarding-step" data-testid="hourly-rate-step">
      <div className="onboarding-step__content">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <div className="onboarding-hero">
            <span className="onboarding-hero__icon">
              <TrendingUp />
            </span>
            <div>
              <p className="onboarding-hero__title">Set your consulting rate</p>
              <p className="onboarding-hero__subtitle">
                Kandir matches buyers with experts like you for paid engagements. We'll surface opportunities that fit
                your background — you only get contacted for deals worth your time.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <span className="onboarding-rate__label">Common rates</span>
          <div className="onboarding-rate__presets">
            {PRESET_RATES.map((preset) => {
              const selected = rateValue === preset.toString();
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  className={['onboarding-rate__preset', selected ? 'onboarding-rate__preset--selected' : '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  ${preset}
                  <span className="onboarding-rate__preset-unit">/hr</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <div className="onboarding-rate__input-card">
            <div className="onboarding-rate__input-label">
              <span className="onboarding-rate__input-label-eyebrow">Your rate</span>
              <p className="onboarding-rate__input-label-helper">Enter a custom amount or pick one above</p>
            </div>
            <TextField
              className="onboarding-rate__input"
              value={rateValue}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                setRateValue(val);
                setError(null);
              }}
              type="text"
              inputMode="numeric"
              size="medium"
              placeholder="150"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography className="onboarding-rate__input-adornment">$</Typography>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" className="onboarding-rate__input-suffix">
                      /hr
                    </Typography>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleContinue()}
              autoFocus
              error={!isValid}
            />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="onboarding-rate__value-props">
            {VALUE_PROPS.map((text, i) => (
              <div key={i} className="onboarding-rate__value-prop">
                <CheckCircle />
                <Typography variant="body2">{text}</Typography>
              </div>
            ))}
          </div>
        </motion.div>

        {error && (
          <Alert severity="error" className="mt-16" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </div>

      <div className="onboarding-step__actions">
        {onBack ? (
          <Button onClick={onBack} startIcon={<ArrowBack />} color="inherit" disabled={isSaving}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <div className="onboarding-step__actions-right">
          <Button onClick={onSkip} variant="text" color="inherit" disabled={isSaving} className="onboarding-btn--skip">
            Skip
          </Button>
          <Button
            onClick={handleContinue}
            variant="contained"
            endIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
            className="onboarding-btn--cta"
            disabled={isSaving || !isValid}
          >
            {isSaving ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};
